import { useState } from 'react';

import { ChevronLeft, ExternalLink, InfoIcon } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { Button } from '@/components/ui/button';
import { ButtonLoadable } from '@/components/ui/button-loadable';
import { Form } from '@/components/ui/form';
import { SimpleTooltip } from '@/components/ui/simple-tooltip';
import { StatusBadge, StatusBadgeType } from '@/modules/common';
import { getInvoiceStatusText } from '@/modules/common/components/molecules/badge/status-configs/invoice-status-config';
import { FullPageErrorState } from '@/modules/common/components/organisms/full-page-error-state';
import { QUICKBOOKS_INVOICE_URL } from '@/modules/finance/common/constants/quickbooks-urls';
import { UnsavedChangesModal } from '@/modules/settings/components/unsaved-changes-modal';

import { InvoiceDetailsForm } from '../components/details/invoice-details-form';
import { InvoiceDetailsSkeleton } from '../components/details/invoice-details-skeleton';
import { InvoiceErrorModal } from '../components/modals/invoice-error-modal';
import { useInvoiceDetailsPage } from '../hooks';

export const InvoiceDetailsPage = () => {
  const { t } = useTranslation(['authenticated.invoices', 'common']);
  const {
    details,
    form,
    isLoading,
    isError,
    isReadOnly,
    isUpdating,
    navigateBack,
    cancelEdit,
    submit,
    unsavedChanges,
  } = useInvoiceDetailsPage();
  const [isErrorModalOpen, setIsErrorModalOpen] = useState(false);

  if (isError || (!isLoading && !details)) {
    return (
      <FullPageErrorState
        title={t('details.error.title')}
        description={t('details.error.description')}
        actions={
          <Button variant="secondary" onClick={navigateBack}>
            {t('details.error.back')}
          </Button>
        }
      />
    );
  }

  const title = details?.referenceNumber
    ? t('details.title', { referenceNumber: details.referenceNumber })
    : t('details.titleWithoutReference');
  const externalId = details?.externalId;

  return (
    <div className="flex h-full w-full flex-col overflow-hidden">
      <header className="z-[5] shrink-0 border-b border-border bg-surface px-4 py-3 shadow-[0_1px_4px_0_hsl(var(--border))]">
        <div className="flex items-center justify-between gap-4">
          <div className="flex min-w-0 flex-1 items-center gap-4">
            <Button
              variant="outline"
              size="sm"
              className="h-7 w-7 shrink-0 p-0"
              aria-label={t('common:back')}
              onClick={navigateBack}
            >
              <ChevronLeft className="h-4 w-4" />
            </Button>
            <div className="flex min-w-0 items-center gap-2">
              <h1 className="truncate text-xl font-semibold leading-none text-foreground sm:text-2xl">
                {title}
              </h1>
              {details && (
                <div className="flex shrink-0 items-center gap-1">
                  <StatusBadge
                    type={StatusBadgeType.Invoice}
                    status={details.status}
                    label={getInvoiceStatusText(details.status)}
                  />
                  {details.syncError && (
                    <SimpleTooltip content={t('details.viewError')}>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-7 w-7 text-muted-foreground"
                        aria-label={t('details.viewError')}
                        onClick={() => setIsErrorModalOpen(true)}
                      >
                        <InfoIcon className="h-4 w-4" />
                      </Button>
                    </SimpleTooltip>
                  )}
                </div>
              )}
            </div>
          </div>
          <div className="flex shrink-0 items-center gap-2">
            {externalId && (
              <Button
                variant="outline"
                size="sm"
                onClick={() => window.open(QUICKBOOKS_INVOICE_URL(externalId), '_blank')}
              >
                <ExternalLink />
                {t('details.openInQuickBooks')}
              </Button>
            )}
          </div>
        </div>
      </header>

      <main className="min-h-0 flex-1 overflow-y-auto px-4 py-6">
        {isLoading ? <InvoiceDetailsSkeleton /> : null}
        {!isLoading && details ? (
          <Form {...form}>
            <form id="invoice-details-form" onSubmit={submit}>
              <InvoiceDetailsForm isReadOnly={isReadOnly} details={details} form={form} />
            </form>
          </Form>
        ) : null}
      </main>

      {!isReadOnly && (
        <footer className="flex shrink-0 justify-end gap-2 border-t border-border bg-surface px-4 py-3">
          <Button
            variant="outline"
            onClick={cancelEdit}
            disabled={!form.formState.isDirty || isUpdating}
          >
            {t('common:cancel')}
          </Button>
          <ButtonLoadable
            type="submit"
            form="invoice-details-form"
            isLoading={isUpdating}
            disabled={!form.formState.isDirty || !form.formState.isValid}
          >
            {t('details.edit.save')}
          </ButtonLoadable>
        </footer>
      )}

      <InvoiceErrorModal
        isOpen={isErrorModalOpen}
        onClose={() => setIsErrorModalOpen(false)}
        syncOperation={details?.syncOperation ?? null}
        syncError={details?.syncError ?? null}
      />
      <UnsavedChangesModal
        open={unsavedChanges.isModalOpen}
        onClose={unsavedChanges.handleModalCancel}
        onDiscard={unsavedChanges.handleModalDiscard}
      />
    </div>
  );
};
